/**
 * Capa Lógica (BL) para la dinámica "Medallas Tradiciones Perronas 2026" (tpx2026 / Tile 52).
 * Gestiona validaciones de aficionado, enmascaramiento, persistencia con TTL,
 * y renderizado de tarjeta dorada de alta resolución en Canvas 2D.
 */

import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import { INTERAKTIA_LOGO_RAW_BASE64 } from '../../assets/interaktiaLogoBase64';
import {
    GetPublicConfig,
    RegisterParticipant,
    VerifyToken,
    TrackMetric as DLTrackMetric,
    LogScreenshot,
    RecoverPass,
    GetAdminMetrics,
    GetAdminParticipants,
    GetAdminConfig,
    UpdateAdminConfig,
    GetExportDataset,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    UnblockTicket,
    UnblockSession,
    RedeemParticipant,
    UnredeemParticipant,
    ResetAllRedeemed,
    SeedMedals,
    ResetDynamicData,
    ResetPassTpxMetrics
} from '../DL/Xtp';



export const PASSES_STORAGE_KEY = 'tpx2026_my_passes';
export const SESSION_STORAGE_KEY = 'xtp_session_id';

/**
 * Obtiene o crea un identificador de sesión persistente durante la navegación (UUID v4).
 * Almacenado en sessionStorage para trazabilidad defensiva sin invadir privacidad.
 * @returns {string} UUID de sesión
 */
export const GetOrCreateSessionId = () => {
    try {
        let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!sessionId) {
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                sessionId = crypto.randomUUID();
            } else {
                sessionId = 'xtp-' + Date.now() + '-' + Math.random().toString(36).substring(2, 11);
            }
            sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
        }
        return sessionId;
    } catch (e) {
        return 'xtp-temp-' + Date.now();
    }
};

/**
 * Despachador omnicanal de métricas para la suite XTP:
 * 1. Envía el evento a la Base de Datos interna (XtpMetrics en PostgreSQL)
 * 2. Empuja el evento a Google Tag Manager (window.dataLayer)
 * 3. Notifica a Google Analytics 4 (window.gtag)
 * 
 * @param {string} eventName - Nombre del evento (ej. 'page_view_passtpx', 'search_query_passtpx', etc.)
 * @param {string} source - Origen del tráfico ('direct_pass_lookup', 'qr_passtpx_direct', etc.)
 * @param {string|null} sessionId - ID de sesión
 */
const TrackMetric = async (eventName, source = 'xolo_shop_local', sessionId = null) => {
    const sId = sessionId || GetOrCreateSessionId();

    // 1. Base de Datos interna de Interaktia
    try {
        DLTrackMetric(eventName, source, sId);
    } catch (e) {
        // Silencioso
    }

    // 2. Google Tag Manager (dataLayer)
    try {
        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            const isPassTpx = eventName.includes('passtpx') || eventName.includes('pass_lookup');
            const gtmEvent = isPassTpx 
                ? (eventName.startsWith('passtpx_') ? eventName : `passtpx_${eventName}`)
                : (eventName.startsWith('tpx2026_') ? eventName : `tpx2026_${eventName}`);

            window.dataLayer.push({
                event: gtmEvent,
                metric_name: eventName,
                session_id: sId,
                traffic_source: source,
                module: isPassTpx ? 'passtpx_fan' : 'tpx2026_fan',
                timestamp: new Date().toISOString()
            });
        }
    } catch (e) {
        // Silencioso
    }

    // 3. Google Analytics 4 (gtag)
    try {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            const isPassTpx = eventName.includes('passtpx') || eventName.includes('pass_lookup');
            const gaEvent = isPassTpx 
                ? (eventName.startsWith('passtpx_') ? eventName : `passtpx_${eventName}`)
                : (eventName.startsWith('tpx2026_') ? eventName : `tpx2026_${eventName}`);

            window.gtag('event', gaEvent, {
                session_id: sId,
                event_category: isPassTpx ? 'passtpx_dynamic' : 'tpx2026_dynamic',
                event_label: eventName,
                traffic_source: source
            });
        }
    } catch (e) {
        // Silencioso
    }
};

/**
 * Valida minuciosamente los campos del formulario de registro móvil del aficionado.
 * @param {object} formData - Datos ingresados por el aficionado
 * @param {object} [config] - Configuración activa de la campaña
 * @returns {{ isValid: boolean, error: string | null, errors: object }}
 */
export const ValidateParticipantData = (formData, config = null) => {
    const errors = {};
    if (!formData) {
        return {
            isValid: false,
            error: 'Por favor completa los campos del formulario.',
            errors: { general: 'Formulario vacío' }
        };
    }

    const { medalCode, ticketNumber, name, email, phone, acceptedTerms } = formData;
    const itemNoun = config?.itemNoun || 'medalla';

    // 1. Código de Medalla (solo si la dinámica requiere validación contra whitelist)
    if (config?.requireWhitelistMedalValidation) {
        if (!medalCode || typeof medalCode !== 'string' || medalCode.trim().length === 0) {
            errors.medalCode = `El código de la ${itemNoun.toLowerCase()} es obligatorio.`;
        }
    }

    // 2. Número de Ticket
    if (!ticketNumber || typeof ticketNumber !== 'string' || ticketNumber.trim().length === 0) {
        errors.ticketNumber = 'El número de boleto o ticket es obligatorio.';
    } else if (config?.ticketRequiredLength && config.ticketRequiredLength > 0) {
        if (ticketNumber.trim().length !== config.ticketRequiredLength) {
            errors.ticketNumber = `El número de ticket debe contener exactamente ${config.ticketRequiredLength} caracteres.`;
        }
    }

    // 3. Nombre Completo (>= 3 caracteres)
    if (!name || typeof name !== 'string' || name.trim().length < 3) {
        errors.name = 'Ingresa tu nombre completo (mínimo 3 caracteres).';
    }

    // 4. Correo Electrónico (Regex estándar RFC 5322 simplificado)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || typeof email !== 'string' || !emailRegex.test(email.trim())) {
        errors.email = 'Ingresa un correo electrónico válido.';
    }

    // 5. Teléfono Móvil (Exactamente 10 dígitos numéricos)
    const cleanPhone = phone ? String(phone).replace(/\D/g, '') : '';
    if (cleanPhone.length !== 10) {
        errors.phone = 'Ingresa un teléfono celular a 10 dígitos.';
    }

    // 6. Términos y Condiciones
    if (acceptedTerms !== true) {
        errors.acceptedTerms = 'Debes aceptar los Términos y Condiciones y el Aviso de Privacidad.';
    }

    const errorKeys = Object.keys(errors);
    const isValid = errorKeys.length === 0;
    const firstError = isValid ? null : errors[errorKeys[0]];

    return {
        isValid,
        error: firstError,
        errors
    };
};

/**
 * Enmascara un número telefónico para proteger la privacidad en pantalla (ej. 55****1234).
 * @param {string} phone
 * @returns {string} Teléfono enmascarado
 */
export const MaskPhone = (phone) => {
    if (!phone) return '';
    const clean = String(phone).replace(/\D/g, '');
    if (clean.length === 10) {
        return `${clean.slice(0, 2)}****${clean.slice(6)}`;
    }
    if (clean.length > 4) {
        return `${clean.slice(0, 2)}****${clean.slice(-2)}`;
    }
    return clean;
};

/**
 * Enmascara un correo electrónico para visualización confidencial (ej. c****o@dominio.com).
 * @param {string} email
 * @returns {string} Correo enmascarado
 */
export const MaskEmail = (email) => {
    if (!email || !email.includes('@')) return email || '';
    const [user, domain] = email.split('@');
    if (user.length <= 2) {
        return `${user[0]}*@${domain}`;
    }
    return `${user[0]}****${user[user.length - 1]}@${domain}`;
};

/**
 * Guarda o actualiza un pase digital en localStorage garantizando unicidad por passCode y medalCode.
 * Agrega marca de tiempo savedAt para control de ciclo de vida (TTL).
 * @param {object} passData - Información del pase retornado por el backend
 * @returns {Array} Lista actualizada de pases guardados
 */
export const SavePassToLocal = (passData) => {
    try {
        if (!passData || (!passData.passCode && !passData.medalCode)) {
            return GetStoredPasses();
        }

        const currentPasses = GetStoredPasses();
        const payloadToSave = {
            ...passData,
            savedAt: passData.savedAt || new Date().toISOString()
        };

        const existingIndex = currentPasses.findIndex(p =>
            (passData.passCode && p.passCode === passData.passCode) ||
            (passData.medalCode && p.medalCode === passData.medalCode)
        );

        if (existingIndex >= 0) {
            currentPasses[existingIndex] = {
                ...currentPasses[existingIndex],
                ...payloadToSave,
                updatedAt: new Date().toISOString()
            };
        } else {
            currentPasses.unshift(payloadToSave);
        }

        localStorage.setItem(PASSES_STORAGE_KEY, JSON.stringify(currentPasses));
        return currentPasses;
    } catch (error) {
        console.error('Error al guardar pase en localStorage:', error);
        return [];
    }
};

/**
 * Recupera todos los pases guardados en el dispositivo descartando los que hayan superado el TTL.
 * @param {number} ttlDays - Días de validez de retención local (por defecto 7 días)
 * @returns {Array} Lista de pases válidos no expirados
 */
export const GetStoredPasses = (ttlDays = 7) => {
    try {
        const stored = localStorage.getItem(PASSES_STORAGE_KEY);
        if (!stored) return [];

        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) return [];

        const now = Date.now();
        const ttlMs = ttlDays * 24 * 60 * 60 * 1000;

        const validPasses = parsed.filter(pass => {
            const passDate = pass.savedAt || pass.createdAtUtc;
            if (!passDate) return true; // Mantener si no tiene fecha explícita
            const timeDiff = now - new Date(passDate).getTime();
            return timeDiff <= ttlMs;
        });

        // Si se purgaron pases caducados, actualizar el almacenamiento local
        if (validPasses.length !== parsed.length) {
            localStorage.setItem(PASSES_STORAGE_KEY, JSON.stringify(validPasses));
        }

        return validPasses;
    } catch (error) {
        console.error('Error al leer pases de localStorage:', error);
        return [];
    }
};

/**
 * Revisa el almacenamiento local ('tpx2026_my_passes') y elimina pases cuyo savedAt exceda ttlDays.
 * @param {number} ttlDays - Tiempo de vida en días (por defecto 7)
 * @returns {Array} Lista filtrada de pases válidos no expirados
 */
export const PurgeExpiredPasses = (ttlDays = 7) => {
    try {
        const stored = localStorage.getItem(PASSES_STORAGE_KEY);
        if (!stored) return [];

        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) return [];

        const now = Date.now();
        const ttlMs = ttlDays * 24 * 60 * 60 * 1000;

        const validPasses = parsed.filter(pass => {
            const passDate = pass.savedAt || pass.createdAtUtc;
            if (!passDate) return true;
            const timeDiff = now - new Date(passDate).getTime();
            return timeDiff <= ttlMs;
        });

        // Actualizar el almacenamiento con los pases limpios
        localStorage.setItem(PASSES_STORAGE_KEY, JSON.stringify(validPasses));
        return validPasses;
    } catch (error) {
        console.error('Error al purgar pases expirados de localStorage:', error);
        return [];
    }
};

/**
 * Guarda múltiples pases en almacenamiento local evitando duplicados por passCode (o medalCode).
 * Actualiza la marca savedAt renovando el TTL y retorna el array completo actualizado.
 * @param {Array|object} newPasses - Uno o varios pases retornados por el backend
 * @returns {Array} Lista completa de pases guardados con TTL renovado
 */
export const SaveMultiplePassesToLocal = (newPasses) => {
    try {
        if (!newPasses) return GetStoredPasses();
        const passesArray = Array.isArray(newPasses) ? newPasses : [newPasses];
        if (passesArray.length === 0) return GetStoredPasses();

        const currentPasses = GetStoredPasses();
        const nowIso = new Date().toISOString();

        passesArray.forEach((newPass) => {
            if (!newPass || (!newPass.passCode && !newPass.medalCode)) return;

            const payloadToSave = {
                ...newPass,
                savedAt: nowIso
            };

            const existingIndex = currentPasses.findIndex(p =>
                (newPass.passCode && p.passCode === newPass.passCode) ||
                (newPass.medalCode && p.medalCode === newPass.medalCode)
            );

            if (existingIndex >= 0) {
                currentPasses[existingIndex] = {
                    ...currentPasses[existingIndex],
                    ...payloadToSave,
                    updatedAt: nowIso
                };
            } else {
                currentPasses.unshift(payloadToSave);
            }
        });

        localStorage.setItem(PASSES_STORAGE_KEY, JSON.stringify(currentPasses));
        return currentPasses;
    } catch (error) {
        console.error('Error al guardar múltiples pases en localStorage:', error);
        return [];
    }
};

/**
 * Formatea una fecha UTC al huso horario de México (Tijuana).
 * @param {string|Date} dateValue
 * @returns {string}
 */
export const FormatMexicoDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
        const d = new Date(dateValue);
        if (isNaN(d.getTime())) return 'N/A';
        return d.toLocaleDateString('es-MX', {
            timeZone: 'America/Tijuana',
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }).toUpperCase();
    } catch {
        return String(dateValue);
    }
};

/**
 * Formatea una fecha UTC al huso horario oficial de Tijuana (America/Tijuana) con fecha y hora.
 * Ejemplo: "13/10/2026, 05:05:38 p. m."
 * @param {string|Date} dateValue - Fecha UTC
 * @param {boolean} [includeSeconds=true] - Si incluye segundos
 * @returns {string} Fecha y hora formateada en hora de Tijuana
 */
export const FormatTijuanaDateTime = (dateValue, includeSeconds = true) => {
    if (!dateValue) return 'N/A';
    try {
        const d = new Date(dateValue);
        if (isNaN(d.getTime())) return 'N/A';
        return d.toLocaleString('es-MX', {
            timeZone: 'America/Tijuana',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: includeSeconds ? '2-digit' : undefined,
            hour12: true
        });
    } catch {
        return String(dateValue);
    }
};

/**
 * Convierte un valor de fecha UTC a un objeto Date con la hora exacta de Tijuana (America/Tijuana),
 * ideal para que librerías como SheetJS / Excel creen celdas con tipo Date nativo y formato fecha.
 * @param {string|Date} dateValue
 * @returns {Date|null}
 */
export const ParseTijuanaDateObject = (dateValue) => {
    if (!dateValue) return null;
    try {
        const d = new Date(dateValue);
        if (isNaN(d.getTime())) return null;

        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Tijuana',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });

        const parts = formatter.formatToParts(d);
        const p = {};
        parts.forEach(({ type, value }) => { p[type] = value; });

        return new Date(
            parseInt(p.year, 10),
            parseInt(p.month, 10) - 1,
            parseInt(p.day, 10),
            parseInt(p.hour === '24' ? '0' : p.hour, 10),
            parseInt(p.minute, 10),
            parseInt(p.second, 10)
        );
    } catch {
        return null;
    }
};

/**
 * Generador gráfico Canvas 2D en Alta Resolución (720x1280 px).
 * Construye la Tarjeta Dorada de Certificación con bordes oro fino,
 * membretes oficiales de Club Tijuana Xoloitzcuintles, badge con relieve,
 * datos del titular, código QR con token HMAC y certificado de autenticidad.
 * Detona descarga automática en formato PNG y audita la acción.
 * 
 * @param {object} passData - Datos completos del pase emitido
 * @param {object} options - Opciones adicionales (selector QR, etc.)
 * @returns {Promise<boolean>} Éxito de la operación
 */
export const DownloadPassCard = async (passData, options = {}) => {
    if (!passData) {
        console.error('DownloadPassCard: No se proporcionaron datos de pase.');
        return false;
    }

    try {
        // Determinar banderas de visibilidad prioritarias
        const showQr = options.showPassQr !== undefined 
            ? options.showPassQr 
            : (passData?.showPassQr !== undefined ? passData.showPassQr : true);

        const showUserData = options.showPassUserData !== undefined 
            ? options.showPassUserData 
            : (passData?.showPassUserData !== undefined ? passData.showPassUserData : true);

        const showLocationNote = options.showPassLocationNote !== undefined 
            ? options.showPassLocationNote 
            : (options.hideLocationNote ? false : (options.showPassSecurityBox !== undefined 
                ? options.showPassSecurityBox 
                : (passData?.showPassLocationNote !== undefined ? passData.showPassLocationNote : true)));

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const width = 720;
        const cardMargin = 30;
        const cardW = width - (cardMargin * 2);

        // Altura dinámica según bloques activos
        const hideHeader = options.hideHeader === true;
        const hidePrize = options.hidePrize === true;

        let calculatedH = hideHeader ? 230 : 345; // Margen superior + ¡Felicidades! + Divisor + Texto descriptivo + Shimmer Banner con Folio
        if (showQr) calculatedH += 250; // Contenedor QR 230px + margen 20px
        if (showUserData) calculatedH += 105; // Nombre + Teléfono/Email + Ticket/Folio + Fecha
        if (showLocationNote) calculatedH += 80; // Caja de seguridad + margen
        calculatedH += 115; // "POWERED BY" + logotipo oficial + padding
        calculatedH += cardMargin; // Margen exterior inferior

        const height = Math.max(calculatedH, 480);
        const cardH = height - (cardMargin * 2);

        canvas.width = width;
        canvas.height = height;

        // 1. Fondo Exterior (Imagen fotográfica de fondo u oscura con gradiente)
        const bgImageUrl = options.backgroundImageUrl || options.bgImageUrl;
        let bgDrawn = false;

        if (bgImageUrl) {
            try {
                const bgImg = new Image();
                bgImg.crossOrigin = 'anonymous';
                await new Promise((resolve) => {
                    bgImg.onload = () => {
                        const imgW = bgImg.naturalWidth || bgImg.width;
                        const imgH = bgImg.naturalHeight || bgImg.height;
                        if (imgW && imgH) {
                            const scale = Math.max(width / imgW, height / imgH);
                            const sW = width / scale;
                            const sH = height / scale;
                            const sX = (imgW - sW) / 2;
                            const sY = (imgH - sH) / 2;
                            ctx.drawImage(bgImg, sX, sY, sW, sH, 0, 0, width, height);

                            // Overlay oscuro sutil para que resalte el marco dorado
                            ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
                            ctx.fillRect(0, 0, width, height);
                            bgDrawn = true;
                        }
                        resolve();
                    };
                    bgImg.onerror = () => resolve();
                    bgImg.src = bgImageUrl;
                });
            } catch (e) {
                // Fallback a gradiente
            }
        }

        if (!bgDrawn) {
            const bgGrad = ctx.createLinearGradient(0, 0, width, height);
            bgGrad.addColorStop(0, '#0f172a');
            bgGrad.addColorStop(0.5, '#1e1b4b');
            bgGrad.addColorStop(1, '#020617');
            ctx.fillStyle = bgGrad;
            ctx.fillRect(0, 0, width, height);
        }

        // 2. Tarjeta Blanca Central con Marco Dorado
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(cardMargin, cardMargin, cardW, cardH, 28);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.lineWidth = 8;
        ctx.strokeStyle = '#d4af37';
        ctx.stroke();
        ctx.clip();

        // Alineación centrada global para todos los elementos de texto
        ctx.textAlign = 'center';

        // 3. Encabezado: ¡FELICIDADES! y 4. Divisor (Condicional)
        const prizeName = passData.prizeName || options.prizeName;
        if (!hideHeader) {
            ctx.textAlign = 'center';
            ctx.fillStyle = '#b38728';
            ctx.font = '900 42px Montserrat, "Cinzel", Georgia, sans-serif';
            const headerTitle = options.headerTitle || (passData.isWinner ? '¡FELICIDADES!' : '¡PASE OFICIAL!');
            ctx.fillText(headerTitle, width / 2, 95);

            // 4. Divisor Dorado con Degradado
            const divGrad = ctx.createLinearGradient(80, 115, width - 80, 115);
            divGrad.addColorStop(0, 'transparent');
            divGrad.addColorStop(0.5, '#d4af37');
            divGrad.addColorStop(1, 'transparent');
            ctx.fillStyle = divGrad;
            ctx.fillRect(80, 115, width - 160, 3);

            // 5. Texto Descriptivo
            ctx.fillStyle = '#0f172a';
            ctx.font = '700 17px Montserrat, Arial, sans-serif';
            const descText = passData.isWinner
                ? (passData.winningPrizeDescription || options.winningPrizeDescription || (prizeName ? `¡Felicidades! Has ganado: ${prizeName}` : '¡Ganaste un premio especial con tu Xolo Folio al registrarte!'))
                : (passData.nonWinningMessage || options.nonWinningMessage || 'Tu Xolo Folio ha sido validado y autenticado oficialmente.');
            ctx.fillText(descText, width / 2, 148);
        }

        // 6. Shimmer Banner Dorado con Folio
        const bannerX = 60;
        const bannerY = hideHeader ? 60 : 175;
        const bannerW = width - 120;
        const bannerH = 135;

        const bannerGrad = ctx.createLinearGradient(bannerX, bannerY, bannerX + bannerW, bannerY + bannerH);
        bannerGrad.addColorStop(0, '#b38728');
        bannerGrad.addColorStop(0.5, '#d4af37');
        bannerGrad.addColorStop(1, '#b38728');

        ctx.beginPath();
        ctx.roundRect(bannerX, bannerY, bannerW, bannerH, 16);
        ctx.fillStyle = bannerGrad;
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        let bannerTitle = options.bannerTitle || (passData.isWinner ? '★ PASE DE GANADOR OFICIAL ★' : 'PASE EXCLUSIVO PERSONAL');
        ctx.font = '900 22px Montserrat, Arial, sans-serif';
        ctx.fillText(bannerTitle, width / 2, bannerY + 30);

        ctx.font = '700 15px Montserrat, Arial, sans-serif';
        ctx.fillText('Tradiciones Perronas', width / 2, bannerY + 50);

        if (passData.isWinner && prizeName && !hidePrize) {
            ctx.font = 'bold 14px Montserrat, Arial, sans-serif';
            ctx.fillStyle = '#fef08a';
            ctx.fillText(`Premio: ${prizeName}`, width / 2, bannerY + 68);
        }

        // Badge blanco translúcido con el Folio
        const badgeW = 320;
        const badgeH = 40;
        const badgeX = (width - badgeW) / 2;
        const badgeY = bannerY + (passData.isWinner && prizeName && !hidePrize ? 80 : 66);

        ctx.beginPath();
        ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 10);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
        ctx.fill();

        ctx.fillStyle = '#172554';
        ctx.font = '900 24px "Courier New", monospace';
        ctx.fillText(passData.passCode || '#TPX26-00000', width / 2, badgeY + 28);

        let currentY = bannerY + bannerH + 20;

        // 7. Contenedor de Código QR de Alta Resolución (Condicional)
        if (showQr) {
            const qrContainerSize = 230;
            const qrContainerX = (width - qrContainerSize) / 2;
            const qrContainerY = currentY;

            ctx.beginPath();
            ctx.roundRect(qrContainerX, qrContainerY, qrContainerSize, qrContainerSize, 16);
            ctx.fillStyle = '#f8fafc';
            ctx.fill();
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = '#e2e8f0';
            ctx.stroke();

            // Obtener o generar el SVG del QR
            let svgData = '';
            const selector = options.qrElementSelector || '#xtp-pass-card svg' || '.xtp-pass-qr svg';
            const qrSvgElement = options.qrElement || (document.querySelector ? document.querySelector(selector) : null);

            if (qrSvgElement) {
                svgData = new XMLSerializer().serializeToString(qrSvgElement);
            } else {
                const qrValue = passData.verificationToken || passData.passCode || 'XTP-2026';
                svgData = renderToStaticMarkup(
                    React.createElement(QRCodeSVG, {
                        value: qrValue,
                        size: 200,
                        bgColor: '#ffffff',
                        fgColor: '#172554',
                        level: 'Q',
                        includeMargin: true
                    })
                );
            }

            if (svgData) {
                const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                const svgUrl = URL.createObjectURL(svgBlob);
                const qrImg = new Image();

                await new Promise((resolve) => {
                    qrImg.onload = () => {
                        ctx.drawImage(
                            qrImg,
                            qrContainerX + 12,
                            qrContainerY + 12,
                            qrContainerSize - 24,
                            qrContainerSize - 24
                        );
                        URL.revokeObjectURL(svgUrl);
                        resolve();
                    };
                    qrImg.onerror = () => {
                        URL.revokeObjectURL(svgUrl);
                        resolve();
                    };
                    qrImg.src = svgUrl;
                });

                // Dibujar explícitamente el escudo oficial de Xolos en el centro del QR sobre el Canvas
                try {
                    const centerLogoImg = new Image();
                    centerLogoImg.crossOrigin = 'anonymous';
                    await new Promise((resolve) => {
                        centerLogoImg.onload = () => {
                            const logoSize = 44;
                            const logoX = qrContainerX + (qrContainerSize - logoSize) / 2;
                            const logoY = qrContainerY + (qrContainerSize - logoSize) / 2;
                            ctx.drawImage(centerLogoImg, logoX, logoY, logoSize, logoSize);
                            resolve();
                        };
                        centerLogoImg.onerror = () => resolve();
                        centerLogoImg.src = options.centerLogoUrl || '/img/favicon/Big.png';
                    });
                } catch (e) {
                    // Continuar si falla la carga del logo
                }
            }

            currentY += qrContainerSize + 20;
        }

        // 8. Datos del Aficionado (Condicional)
        if (showUserData) {
            const dataY = currentY + 14;
            ctx.fillStyle = '#0f172a';
            ctx.font = '800 22px Montserrat, Arial, sans-serif';
            const fanName = (passData.name || 'AFICIONADO').toUpperCase();
            ctx.fillText(fanName, width / 2, dataY);

            ctx.fillStyle = '#64748b';
            ctx.font = '500 14px Montserrat, Arial, sans-serif';
            ctx.fillText(`${MaskEmail(passData.email)} | ${MaskPhone(passData.phone)}`, width / 2, dataY + 24);

            ctx.fillStyle = '#0f172a';
            ctx.font = '600 13px Montserrat, monospace, sans-serif';
            ctx.fillText(`Ticket: ${passData.ticketNumber || 'N/A'}`, width / 2, dataY + 48);

            const rawDate = passData.createdAtUtc || passData.createdAt || passData.savedAt;
            const dateStr = FormatMexicoDate(rawDate);
            if (dateStr && dateStr !== 'N/A') {
                ctx.fillStyle = '#64748b';
                ctx.font = '500 12px Montserrat, Arial, sans-serif';
                ctx.fillText(`Registrado el: ${dateStr}`, width / 2, dataY + 68);
            }

            currentY += 105;
        }

        // 9. Caja de Seguridad y Acceso (Condicional)
        if (showLocationNote) {
            const secBoxW = width - 140;
            const secBoxH = 56;
            const secBoxX = (width - secBoxW) / 2;
            const secBoxY = currentY + 5;

            ctx.beginPath();
            ctx.roundRect(secBoxX, secBoxY, secBoxW, secBoxH, 10);
            ctx.fillStyle = '#fef2f2';
            ctx.fill();
            ctx.lineWidth = 1;
            ctx.strokeStyle = '#fecaca';
            ctx.stroke();

            ctx.fillStyle = '#991b1b';
            ctx.font = '800 12px Montserrat, Arial, sans-serif';
            ctx.fillText('MUESTRA ESTE PASE O UNA CAPTURA EN TIENDA OFICIAL O ESTADIO', width / 2, secBoxY + 34);

            currentY += secBoxH + 20;
        }

        // 10. Powered by y Logotipo Oficial
        const sponsorY = currentY + 12;
        ctx.fillStyle = '#94a3b8';
        ctx.font = '700 11px Montserrat, Arial, sans-serif';
        ctx.fillText('POWERED BY', width / 2, sponsorY);

        try {
            const logoImg = new Image();
            await new Promise((resolve) => {
                logoImg.onload = () => {
                    const aspect = (logoImg.naturalWidth && logoImg.naturalHeight)
                        ? (logoImg.naturalWidth / logoImg.naturalHeight)
                        : (logoImg.width && logoImg.height ? logoImg.width / logoImg.height : (160 / 46));
                    const logoH = 46;
                    const logoW = Math.round(logoH * aspect);
                    ctx.drawImage(logoImg, (width - logoW) / 2, sponsorY + 12, logoW, logoH);
                    resolve();
                };
                logoImg.onerror = () => resolve();
                logoImg.src = options.sponsorLogoUrl || '/img/page/xolos-five-black.png';
            });
        } catch (e) {
            // Ignorar si no carga
        }

        ctx.restore();

        // 12. Generar Blob y Detonar Descarga Automática
        const cleanFolio = (passData.passCode || 'PASE').replace(/[^a-zA-Z0-9]/g, '');
        const filename = `Pase_TradicionesPerronas_${cleanFolio}.png`;

        await new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    resolve(false);
                    return;
                }
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.download = filename;
                link.href = url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                resolve(true);
            }, 'image/png');
        });

        // 13. Registrar en Auditoría y Telemetría
        LogScreenshot(passData.participantId || null, passData.passCode, passData.name, 'BUTTON_DOWNLOAD');
        TrackMetric('pass_download_card', passData.source || 'xolo_shop_local', GetOrCreateSessionId());

        return true;
    } catch (error) {
        console.error('Error al generar y descargar la tarjeta en Canvas 2D:', error);
        return false;
    }
};

/**
 * Exporta el reporte oficial estandarizado en Excel (.xlsx) con ExcelJS y membrete corporativo institucional.
 * Hoja 1 (Participantes): Membrete corporativo con logotipo Interaktia, métricas de embudo y padrón oficial de participantes.
 * Hoja 2 (Canjes): Membrete corporativo, métricas de canje y control de entrega de boletos/premios en tienda/estadio.
 * Hoja 3 (Portal passtpx): Membrete corporativo, métricas del portal de aficionados y bitácora de consultas realizadas.
 * @param {object} dataset - Datos consolidados retornados por GetExportDataset()
 * @returns {Promise<boolean>}
 */
export const ExportXtpReportToExcel = async (dataset) => {
    if (!dataset || !dataset.participants) {
        console.error('ExportXtpReportToExcel: Dataset vacío o inválido');
        return false;
    }

    try {
        const { campaignTitle, metrics = {}, participants = [], recoveryLogs = [] } = dataset;
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Interaktia V2 - Five Digital Minds';
        workbook.created = new Date();

        // Registrar imagen corporativa (Logotipo Interaktia) en el Workbook
        let imageId = null;
        if (INTERAKTIA_LOGO_RAW_BASE64) {
            try {
                imageId = workbook.addImage({
                    base64: INTERAKTIA_LOGO_RAW_BASE64,
                    extension: 'jpeg'
                });
            } catch (imgErr) {
                console.warn('Error al registrar imagen corporativa en Excel:', imgErr);
            }
        }

        const tijuanaNowStr = FormatTijuanaDateTime(new Date(), false);
        const pageViews = metrics.totalPageViews || 0;
        const formStarts = metrics.totalFormStarts || 0;
        const registers = metrics.totalRegistersCompleted || participants.length;
        const recoveredPasses = metrics.totalPassesRecovered || 0;
        const commercialCount = participants.filter(p => p.commercialConsent).length;

        // Estilos reutilizables idénticos a la plantilla institucional
        const fontHeaderTitle = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF000000' } };
        const fontHeaderMetaBold = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF000000' } };
        const fontHeaderMetaRegular = { name: 'Calibri', size: 11, color: { argb: 'FF000000' } };
        const fontSectionTitle = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FF000000' } };
        const fontMetricLabel = { name: 'Calibri', size: 10, color: { argb: 'FF000000' } };
        const fontMetricValue = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF000000' } };
        const fontFooterSource = { name: 'Calibri', size: 9, italic: true, color: { argb: 'FF808080' } };
        const fontTableHeader = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        const fontDataCell = { name: 'Calibri', size: 10, color: { argb: 'FF000000' } };

        const fillTableHeader = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF366092' } // Azul corporativo tema institucional
        };

        const borderTableHeader = {
            top: { style: 'thin', color: { argb: 'FFB0C4DE' } },
            bottom: { style: 'thin', color: { argb: 'FFB0C4DE' } },
            left: { style: 'thin', color: { argb: 'FFB0C4DE' } },
            right: { style: 'thin', color: { argb: 'FFB0C4DE' } }
        };

        const borderDataRow = {
            bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } }
        };

        // ----------------------------------------------------
        // HOJA 1: Participantes (Padrón Oficial de Dinámica)
        // ----------------------------------------------------
        const sheet1 = workbook.addWorksheet('Participantes', {
            views: [{ showGridLines: true }]
        });

        // Incrustar logotipo en A1:A6
        if (imageId !== null) {
            sheet1.addImage(imageId, {
                tl: { col: 0.1, row: 0.1 },
                ext: { width: 115, height: 115 }
            });
        }

        // Definir anchos de columnas
        sheet1.columns = [
            { key: 'name', width: 26 },
            { key: 'email', width: 34 },
            { key: 'phone', width: 18 },
            { key: 'ticket', width: 18 },
            { key: 'date', width: 24 },
            { key: 'folio', width: 18 },
            { key: 'marketing', width: 24 },
            { key: 'prize', width: 22 }
        ];

        // Bloque de encabezado institucional (Filas 1 a 5)
        sheet1.getCell('B1').value = 'Informe';
        sheet1.getCell('B1').font = fontHeaderTitle;

        sheet1.getCell('B2').value = `Evento: ${campaignTitle || 'Xolo Shop Venta de Jersey Alterno'}`;
        sheet1.getCell('B2').font = fontHeaderMetaBold;

        sheet1.getCell('B3').value = `Fecha: ${tijuanaNowStr} (Hora de Tijuana)`;
        sheet1.getCell('B3').font = fontHeaderMetaRegular;

        sheet1.getCell('B4').value = 'Dinámica: Registro de tickets (individual)';
        sheet1.getCell('B4').font = fontHeaderMetaRegular;

        sheet1.getCell('B5').value = 'Marca: Xolo Shop';
        sheet1.getCell('B5').font = fontHeaderMetaRegular;

        // Bloque de datos recabados (Filas 8 a 14)
        sheet1.getCell('B8').value = 'Datos recabados';
        sheet1.getCell('B8').font = fontSectionTitle;

        const metricsSheet1 = [
            ['Escaneos de QR de pantalla', pageViews],
            ['Ingresos a la página', pageViews],
            ['Inicios de registro', formStarts],
            ['Registros completados', registers],
            ['Cupones solicitados nuevamente', recoveredPasses],
            ['Usuarios que aceptaron recibir publicidad', commercialCount]
        ];

        metricsSheet1.forEach(([label, val], idx) => {
            const r = 9 + idx;
            const cellLabel = sheet1.getCell(`B${r}`);
            cellLabel.value = label;
            cellLabel.font = fontMetricLabel;

            const cellVal = sheet1.getCell(`C${r}`);
            cellVal.value = val;
            cellVal.font = fontMetricValue;
            cellVal.alignment = { horizontal: 'left' };
        });

        // Pie de fuente (Fila 17)
        sheet1.getCell('B17').value = 'Fuente: datos internos';
        sheet1.getCell('B17').font = fontFooterSource;

        // Título de la tabla (Fila 20)
        sheet1.getCell('A20').value = 'Lista de participantes (completaron la dinámica)';
        sheet1.getCell('A20').font = fontSectionTitle;
        sheet1.mergeCells('A20:H20');
        sheet1.getCell('A20').alignment = { horizontal: 'center', vertical: 'middle' };

        // Encabezados de la tabla (Fila 21)
        const headersSheet1 = [
            'Nombre',
            'Correo Electrónico',
            'Tel / WhatsApp',
            'Ticket / Pulsera',
            'Fecha de participación',
            'Folio del pase',
            'Aceptó mercadotecnia',
            'Premio'
        ];

        const headerRow1 = sheet1.getRow(21);
        headerRow1.height = 28.8;
        headersSheet1.forEach((h, i) => {
            const cell = headerRow1.getCell(i + 1);
            cell.value = h;
            cell.font = fontTableHeader;
            cell.fill = fillTableHeader;
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = borderTableHeader;
        });

        // Filas de datos (Fila 22 en adelante)
        const sortedParticipants = [...participants].sort((a, b) => new Date(a.createdAtUtc) - new Date(b.createdAtUtc));
        sortedParticipants.forEach((p, idx) => {
            const r = 22 + idx;
            const row = sheet1.getRow(r);
            row.height = 20;

            const partDate = FormatTijuanaDateTime(p.createdAtUtc, true);
            const marketingVal = p.commercialConsent ? FormatTijuanaDateTime(p.createdAtUtc, true) : 'No';
            const prizeVal = p.isWinner ? (p.prizeName || 'Boleto Ganador') : 'Sin premio';

            row.values = [
                p.name || '',
                p.email || '',
                p.phone || '',
                p.ticketNumber || p.medalCode || '',
                partDate,
                p.passCode || '',
                marketingVal,
                prizeVal
            ];

            for (let c = 1; c <= 8; c++) {
                const cell = row.getCell(c);
                cell.font = fontDataCell;
                cell.alignment = { vertical: 'middle' };
                cell.border = borderDataRow;
            }
        });

        // ----------------------------------------------------
        // HOJA 2: Canjes (Control de Entrega y Redención de Premios)
        // ----------------------------------------------------
        const sheetCanjes = workbook.addWorksheet('Canjes', {
            views: [{ showGridLines: true }]
        });

        // Incrustar logotipo en A1:A6
        if (imageId !== null) {
            sheetCanjes.addImage(imageId, {
                tl: { col: 0.1, row: 0.1 },
                ext: { width: 115, height: 115 }
            });
        }

        // Definir anchos de columnas
        sheetCanjes.columns = [
            { key: 'name', width: 26 },
            { key: 'email', width: 34 },
            { key: 'phone', width: 18 },
            { key: 'ticket', width: 18 },
            { key: 'date', width: 24 },
            { key: 'folio', width: 18 },
            { key: 'prize', width: 26 },
            { key: 'redeemStatus', width: 18 },
            { key: 'redeemDate', width: 26 },
            { key: 'redeemedBy', width: 24 }
        ];

        // Bloque de encabezado institucional (Filas 1 a 5)
        sheetCanjes.getCell('B1').value = 'Informe';
        sheetCanjes.getCell('B1').font = fontHeaderTitle;

        sheetCanjes.getCell('B2').value = `Evento: ${campaignTitle || 'Xolo Shop Venta de Jersey Alterno'}`;
        sheetCanjes.getCell('B2').font = fontHeaderMetaBold;

        sheetCanjes.getCell('B3').value = `Fecha: ${tijuanaNowStr} (Hora de Tijuana)`;
        sheetCanjes.getCell('B3').font = fontHeaderMetaRegular;

        sheetCanjes.getCell('B4').value = 'Dinámica: Control y Registro de Canjes en Tienda / Estadio';
        sheetCanjes.getCell('B4').font = fontHeaderMetaRegular;

        sheetCanjes.getCell('B5').value = 'Marca: Xolo Shop';
        sheetCanjes.getCell('B5').font = fontHeaderMetaRegular;

        // Bloque de datos recabados de canje (Filas 8 a 14)
        sheetCanjes.getCell('B8').value = 'Datos recabados (Control de Canjes)';
        sheetCanjes.getCell('B8').font = fontSectionTitle;

        const totalWinners = participants.filter(p => p.isWinner).length;
        const totalRedeemed = metrics.totalRedeemed !== undefined ? metrics.totalRedeemed : participants.filter(p => p.hasRedeemed).length;
        const pendingRedeem = participants.filter(p => !p.hasRedeemed && !p.isDisqualified).length;
        const totalDisqualified = metrics.totalDisqualified !== undefined ? metrics.totalDisqualified : participants.filter(p => p.isDisqualified).length;
        const redeemRateStr = totalWinners > 0 ? `${((totalRedeemed / totalWinners) * 100).toFixed(1)}%` : 'N/A';

        const metricsSheetCanjes = [
            ['Total de participantes registrados', registers],
            ['Boletos ganadores asignados', totalWinners],
            ['Boletos entregados / canjeados', totalRedeemed],
            ['Boletos pendientes de canje', pendingRedeem],
            ['Pases descalificados / anulados', totalDisqualified],
            ['Efectividad de canje sobre ganadores', redeemRateStr]
        ];

        metricsSheetCanjes.forEach(([label, val], idx) => {
            const r = 9 + idx;
            const cellLabel = sheetCanjes.getCell(`B${r}`);
            cellLabel.value = label;
            cellLabel.font = fontMetricLabel;

            const cellVal = sheetCanjes.getCell(`C${r}`);
            cellVal.value = val;
            cellVal.font = fontMetricValue;
            cellVal.alignment = { horizontal: 'left' };
        });

        // Pie de fuente (Fila 17)
        sheetCanjes.getCell('B17').value = 'Fuente: datos internos';
        sheetCanjes.getCell('B17').font = fontFooterSource;

        // Título de la tabla (Fila 20)
        sheetCanjes.getCell('A20').value = 'Lista oficial de participantes y control de canjes';
        sheetCanjes.getCell('A20').font = fontSectionTitle;
        sheetCanjes.mergeCells('A20:J20');
        sheetCanjes.getCell('A20').alignment = { horizontal: 'center', vertical: 'middle' };

        // Encabezados de la tabla (Fila 21)
        const headersSheetCanjes = [
            'Nombre',
            'Correo Electrónico',
            'Tel / WhatsApp',
            'Ticket / Pulsera',
            'Fecha de participación',
            'Folio del pase',
            'Premio',
            'Estado de canje',
            'Fecha de canje',
            'Validado por (Staff)'
        ];

        const headerRowCanjes = sheetCanjes.getRow(21);
        headerRowCanjes.height = 28.8;
        headersSheetCanjes.forEach((h, i) => {
            const cell = headerRowCanjes.getCell(i + 1);
            cell.value = h;
            cell.font = fontTableHeader;
            cell.fill = fillTableHeader;
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = borderTableHeader;
        });

        // Filas de datos (Fila 22 en adelante)
        const sortedCanjes = [...participants].sort((a, b) => new Date(a.createdAtUtc) - new Date(b.createdAtUtc));
        sortedCanjes.forEach((p, idx) => {
            const r = 22 + idx;
            const row = sheetCanjes.getRow(r);
            row.height = 20;

            const partDate = FormatTijuanaDateTime(p.createdAtUtc, true);
            const prizeVal = p.isWinner ? (p.prizeName || 'Boleto Ganador') : 'Sin premio';

            let statusCanje = 'Pendiente';
            if (p.isDisqualified) {
                statusCanje = 'Descalificado';
            } else if (p.hasRedeemed) {
                statusCanje = 'Canjeado';
            }

            const redeemDateVal = p.hasRedeemed && p.redeemedAtUtc
                ? FormatTijuanaDateTime(p.redeemedAtUtc, true)
                : (p.isDisqualified ? 'N/A' : 'Pendiente');

            const validatedByVal = p.hasRedeemed
                ? (p.redeemedBy || 'Staff Operativo')
                : (p.isDisqualified ? 'Descalificado' : '-');

            row.values = [
                p.name || '',
                p.email || '',
                p.phone || '',
                p.ticketNumber || p.medalCode || '',
                partDate,
                p.passCode || '',
                prizeVal,
                statusCanje,
                redeemDateVal,
                validatedByVal
            ];

            for (let c = 1; c <= 10; c++) {
                const cell = row.getCell(c);
                cell.font = fontDataCell;
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: (c === 3 || c === 4 || c === 5 || c === 6 || c === 8 || c === 9) ? 'center' : 'left'
                };
                cell.border = borderDataRow;

                // Destacar visualmente el estado de canje
                if (c === 8) {
                    if (p.hasRedeemed) {
                        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF006100' } };
                    } else if (p.isDisqualified) {
                        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF9C0006' } };
                    }
                }
            }
        });

        // ----------------------------------------------------
        // HOJA 3: Portal passtpx (Métricas y bitácora de consultas)
        // ----------------------------------------------------
        const sheet3 = workbook.addWorksheet('Portal passtpx', {
            views: [{ showGridLines: true }]
        });

        // Incrustar logotipo en A1:A6
        if (imageId !== null) {
            sheet3.addImage(imageId, {
                tl: { col: 0.1, row: 0.1 },
                ext: { width: 115, height: 115 }
            });
        }

        // Definir anchos de columnas
        sheet3.columns = [
            { key: 'idx', width: 8 },
            { key: 'date', width: 24 },
            { key: 'fan', width: 26 },
            { key: 'folio', width: 18 },
            { key: 'query', width: 24 },
            { key: 'type', width: 16 },
            { key: 'success', width: 14 },
            { key: 'ip', width: 18 },
            { key: 'ua', width: 35 }
        ];

        // Encabezado institucional Hoja 3
        sheet3.getCell('B1').value = 'Informe';
        sheet3.getCell('B1').font = fontHeaderTitle;

        sheet3.getCell('B2').value = `Evento: ${campaignTitle || 'Xolo Shop Venta de Jersey Alterno'}`;
        sheet3.getCell('B2').font = fontHeaderMetaBold;

        sheet3.getCell('B3').value = `Fecha: ${tijuanaNowStr} (Hora de Tijuana)`;
        sheet3.getCell('B3').font = fontHeaderMetaRegular;

        sheet3.getCell('B4').value = 'Dinámica: Portal de consulta y descarga de pases (/passtpx)';
        sheet3.getCell('B4').font = fontHeaderMetaRegular;

        sheet3.getCell('B5').value = 'Marca: Xolo Shop';
        sheet3.getCell('B5').font = fontHeaderMetaRegular;

        // Métricas recabadas de /passtpx (Filas 8 a 16)
        sheet3.getCell('B8').value = 'Datos recabados (Portal /passtpx)';
        sheet3.getCell('B8').font = fontSectionTitle;

        const passFan = metrics.passFanMetrics || metrics.passFan || {};
        const passFanQr = passFan.totalQrScans || 0;
        const passFanViews = passFan.totalPageViews || 0;
        const passFanStarts = passFan.totalSearchStarts || 0;
        const passFanQueries = passFan.totalSearchQueries || 0;
        const passFanFound = passFan.totalPassesFound || 0;
        const passFanNotFound = passFan.totalNotFound || 0;
        const passFanSaved = passFan.totalPassesSaved || 0;
        const passFanRate = passFan.searchSuccessRatePercent !== undefined ? `${passFan.searchSuccessRatePercent}%` : '0.0%';

        const metricsSheet3 = [
            ['Escaneos QR al portal (?s=qr)', passFanQr],
            ['Ingresos totales al portal (/passtpx)', passFanViews],
            ['Inicios de búsqueda', passFanStarts],
            ['Consultas realizadas', passFanQueries],
            ['Pases encontrados exitosamente', passFanFound],
            ['Consultas sin coincidencia', passFanNotFound],
            ['Pases guardados en imagen', passFanSaved],
            ['Tasa de efectividad de búsqueda (%)', passFanRate]
        ];

        metricsSheet3.forEach(([label, val], idx) => {
            const r = 9 + idx;
            const cellLabel = sheet3.getCell(`B${r}`);
            cellLabel.value = label;
            cellLabel.font = fontMetricLabel;

            const cellVal = sheet3.getCell(`C${r}`);
            cellVal.value = val;
            cellVal.font = fontMetricValue;
            cellVal.alignment = { horizontal: 'left' };
        });

        // Pie de fuente (Fila 18)
        sheet3.getCell('B18').value = 'Fuente: datos internos';
        sheet3.getCell('B18').font = fontFooterSource;

        // Título de la tabla (Fila 21)
        sheet3.getCell('A21').value = 'Historial de consultas y recuperaciones de pases (Portal /passtpx)';
        sheet3.getCell('A21').font = fontSectionTitle;
        sheet3.mergeCells('A21:I21');
        sheet3.getCell('A21').alignment = { horizontal: 'center', vertical: 'middle' };

        // Encabezados de la tabla (Fila 22)
        const headersSheet3 = [
            '#',
            'Fecha y Hora',
            'Aficionado',
            'Folio Localizado',
            'Dato Consultado',
            'Tipo',
            '¿Exitoso?',
            'Dirección IP',
            'Dispositivo'
        ];

        const headerRow3 = sheet3.getRow(22);
        headerRow3.height = 28.8;
        headersSheet3.forEach((h, i) => {
            const cell = headerRow3.getCell(i + 1);
            cell.value = h;
            cell.font = fontTableHeader;
            cell.fill = fillTableHeader;
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = borderTableHeader;
        });

        // Filas de bitácora (Fila 23 en adelante)
        if (recoveryLogs && recoveryLogs.length > 0) {
            recoveryLogs.forEach((l, idx) => {
                const r = 23 + idx;
                const row = sheet3.getRow(r);
                row.height = 20;

                const recDate = FormatTijuanaDateTime(l.createdAtUtc, true);
                row.values = [
                    idx + 1,
                    recDate,
                    l.name || 'N/A',
                    l.passCode || 'N/A',
                    l.queryUsed || '',
                    l.searchType || 'AUTO',
                    l.wasSuccessful ? 'SÍ' : 'NO',
                    l.clientIp || '127.0.0.1',
                    l.userAgent || 'N/A'
                ];

                for (let c = 1; c <= 9; c++) {
                    const cell = row.getCell(c);
                    cell.font = fontDataCell;
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: c === 1 || c === 7 ? 'center' : 'left'
                    };
                    cell.border = borderDataRow;
                }
            });
        } else {
            const emptyRow = sheet3.getRow(23);
            emptyRow.height = 24;
            emptyRow.getCell(1).value = 'No hay registros de consultas en la bitácora aún.';
            emptyRow.getCell(1).font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF808080' } };
            sheet3.mergeCells('A23:I23');
            emptyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
        }

        // Generar archivo binario y descargar en el navegador
        const buffer = await workbook.xlsx.writeBuffer();
        const dateStr = new Date().toISOString().slice(0, 10);
        const cleanCampaign = (campaignTitle || 'Tradiciones_Perronas')
            .replace(/[^a-zA-Z0-9_-]/g, '_')
            .substring(0, 30);
        const filename = `Reporte_${cleanCampaign}_Oficial_${dateStr}.xlsx`;

        if (typeof window !== 'undefined' && typeof window.document !== 'undefined') {
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(url);
        }

        return true;
    } catch (error) {
        console.error('Error al generar reporte Excel con ExcelJS:', error);
        return false;
    }
};

/**
 * Sintetizador acústico nativo usando Web Audio API.
 * 100% offline, 0 archivos de audio externos, latencia cero (HU-06).
 * @param {'success' | 'warning' | 'error'} type
 */
export const PlayXtpAudio = (type = 'success') => {
    try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;
        const ctx = new AudioContextClass();

        if (ctx.state === 'suspended') {
            ctx.resume();
        }

        const now = ctx.currentTime;

        if (type === 'success') {
            // Arpegio mayor ascendente C5 -> E5 -> G5 (triunfal)
            const notes = [523.25, 659.25, 783.99];
            notes.forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, now + i * 0.08);

                gain.gain.setValueAtTime(0, now + i * 0.08);
                gain.gain.linearRampToValueAtTime(0.25, now + i * 0.08 + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.22);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(now + i * 0.08);
                osc.stop(now + i * 0.08 + 0.25);
            });
        } else if (type === 'warning') {
            // Bitono de alerta A4 -> E4 (advertencia / ya canjeado)
            const notes = [440.0, 329.63];
            notes.forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(freq, now + i * 0.12);

                gain.gain.setValueAtTime(0, now + i * 0.12);
                gain.gain.linearRampToValueAtTime(0.3, now + i * 0.12 + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.18);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(now + i * 0.12);
                osc.stop(now + i * 0.12 + 0.2);
            });
        } else if (type === 'error') {
            // Tono grave disonante 180Hz -> 90Hz (inválido / descalificado)
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(180, now);
            osc.frequency.exponentialRampToValueAtTime(90, now + 0.35);

            gain.gain.setValueAtTime(0.35, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(now);
            osc.stop(now + 0.4);
        }
    } catch (err) {
        // Silencioso si el navegador bloquea audio antes de interacción
        console.warn('AudioContext no disponible o bloqueado:', err);
    }
};

export {
    GetPublicConfig,
    RegisterParticipant,
    VerifyToken,
    TrackMetric,
    LogScreenshot,
    RecoverPass,
    GetAdminMetrics,
    GetAdminParticipants,
    GetAdminConfig,
    UpdateAdminConfig,
    GetExportDataset,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    UnblockTicket,
    UnblockSession,
    RedeemParticipant,
    UnredeemParticipant,
    ResetAllRedeemed,
    SeedMedals,
    ResetDynamicData,
    ResetPassTpxMetrics
};



